// Lib
import { useEffect } from 'react';

// Utils
import { getTableCellEditorId, getTableGridEditorId } from '../utils/tableCellEditingUtils';

/**
 * Custom hook for managing table selection side effects
 *
 * @param {Object} props - Hook configuration
 */
export const useTableSelectionEffects = ({
    elementId,
    currentlyEditingEditorId,
    hotTableInstanceRef,
    isSingleSelected,
    isResizing,
    isReadOnly,
}) => {
    /**
     * Handsontable should only listen to keyboard shortcuts when there is one or more cell selections in the table
     */
    useEffect(() => {
        if (!hotTableInstanceRef.current || isReadOnly) return;

        const handleSelectionListen = () => {
            const hasSelections = !!hotTableInstanceRef.current.getSelected()?.length;

            if (isSingleSelected && hasSelections) {
                hotTableInstanceRef.current.listen();
            }
        };

        const handleDeselectUnlisten = () => {
            // HOT can fire `afterDeselect` before its internal selection state and focus bookkeeping have fully
            // settled, so defer the unlisten until the current callback stack has completed.
            setTimeout(() => {
                const hasSelections = !!hotTableInstanceRef.current.getSelected()?.length;
                if (hasSelections) return;

                hotTableInstanceRef.current.unlisten();
            });
        };

        hotTableInstanceRef.current.addHook('afterSelectionEnd', handleSelectionListen);
        hotTableInstanceRef.current.addHook('afterDeselect', handleDeselectUnlisten);

        return () => {
            hotTableInstanceRef.current?.removeHook('afterSelectionEnd', handleSelectionListen);
            hotTableInstanceRef.current?.removeHook('afterDeselect', handleDeselectUnlisten);
        };
    }, [hotTableInstanceRef, isReadOnly, isSingleSelected]);

    /**
     * Deselect cells in table when the table is no longer in a state where the grid should stay active, such as:
     * - When selection has moved to table element title/caption
     * - When table element is no longer selected
     */
    useEffect(() => {
        if (!hotTableInstanceRef.current || isResizing) return;

        const isEditingCellOrGrid =
            !currentlyEditingEditorId ||
            currentlyEditingEditorId === getTableGridEditorId(elementId) ||
            currentlyEditingEditorId === getTableCellEditorId(elementId);

        if (!isReadOnly && isSingleSelected && isEditingCellOrGrid) return;

        hotTableInstanceRef.current.deselectCell();
    }, [elementId, currentlyEditingEditorId, hotTableInstanceRef, isReadOnly, isResizing, isSingleSelected]);
};

export default useTableSelectionEffects;
